import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, lastValueFrom } from 'rxjs';
import { VenuesApiService } from './venues-api.service';
import { VenueResponse } from '../../shared/models/VenueResponse';
import { Venue } from '../../shared/classes/Venue';
import { OrderMode } from '../../shared/enums/order/OrderMode';
import { sortVenuesByDistance } from '../../shared/utils/geo';
import { VenuesByOrder } from '../../shared/models/VenuesByOrder';
import { Address } from '../../shared/classes/Address';
import { Legal } from '../../shared/models/Legal';

@Injectable({
	providedIn: 'root',
})
export class VenuesStateService {
	readonly initialVenuesByOrderMode: VenuesByOrder = {
		[OrderMode.INSIDE]: [],
		[OrderMode.DELIVERY]: {
			all: [],
			byDeliveryAddress: [],
		},
		[OrderMode.TAKE_AWAY]: [],
		[OrderMode.PARK_COLLECT]: [],
	};

	allVenues$: BehaviorSubject<Venue[]> = new BehaviorSubject([]);
	venuesByOrderMode$: BehaviorSubject<VenuesByOrder> = new BehaviorSubject(
		structuredClone(this.initialVenuesByOrderMode)
	);
	selectedVenue$: BehaviorSubject<Venue> = new BehaviorSubject(null);
	selectedOrderMode$ = new BehaviorSubject(OrderMode.INSIDE);

	legal: Legal;

	private venuesApi = inject(VenuesApiService);
	constructor() {}

	actualVenues(deliveryAddress: Address) {
		const actualVenues = this.venuesByOrderMode$.value[this.selectedOrderMode$.value];
		if (this.selectedOrderMode$.value === OrderMode.DELIVERY) {
			const deliveryVenues = actualVenues as VenuesByOrder[OrderMode.DELIVERY];
			return deliveryAddress ? deliveryVenues.byDeliveryAddress : deliveryVenues.all;
		} else return actualVenues as Venue[];
	}

	isVenueAvailable(venue: VenueResponse) {
		if (!venue.isServiceActivated || !venue.isPublished) {
			return false;
		}
		if (!venue?.location?.coordinates[0] || !venue?.location?.coordinates[1]) return false;
		return !venue.isMaster;
	}

	async initVenues(userAddress: Address) {
		const sortedVenuesByOrderMode = structuredClone(this.initialVenuesByOrderMode);
		let allVenues: Venue[] = this.allVenues$.value;

		if (!allVenues.length) {
			const responseVenues: VenueResponse[] = await lastValueFrom(this.venuesApi.getAllVenues());
			responseVenues.forEach((venueRes): any => {
				if (!this.isVenueAvailable(venueRes)) return;
				allVenues.push(new Venue(venueRes, userAddress, this.selectedOrderMode$.value));
			});
		}

		if (userAddress) {
			allVenues.forEach(v => (v.distanceToUser = v.computeDistanceToUser(userAddress)));
			allVenues = sortVenuesByDistance(allVenues);
		}

		allVenues.forEach((venue: Venue): any => {
			if (venue.preorderTakeAway) sortedVenuesByOrderMode[OrderMode.TAKE_AWAY].push(venue);
			if (venue.preorderIn) sortedVenuesByOrderMode[OrderMode.INSIDE].push(venue);
			if (venue.deliveryEnabled) {
				// if (venue.deliveryByRadius && venue.distanceToUser !== undefined && venue.distanceToUser > venue.deliveryRadius) {
				//     return false;
				// }
				// if (venue.deliveryPostalCodes && !venue.deliveryByRadius && !venue.isPostalDelivery) {
				//     return false;
				// }
				sortedVenuesByOrderMode[OrderMode.DELIVERY].all.push(venue);
			}
		});

		this.allVenues$.next(allVenues);
		this.selectedVenue$.next(userAddress ? sortedVenuesByOrderMode[this.selectedOrderMode$.value]?.[0] : null);
		this.venuesByOrderMode$.next(sortedVenuesByOrderMode);
	}

	async loadVenuesForDeliveryAddress(address: Address) {
		let responseVenues: VenueResponse[] = await lastValueFrom(
			this.venuesApi.getVenuesByGeo(address.lat, address.lng, 25, false)
		);
		let venues = responseVenues
			.filter(vR => this.isVenueAvailable(vR))
			.map(vR => new Venue(vR, address, this.selectedOrderMode$.value));
		venues = sortVenuesByDistance(venues);

		return venues;
	}

	initVenuesForDeliveryAddress(venues: Venue[]) {
		const venuesByOrderMode = this.venuesByOrderMode$.value;
		venuesByOrderMode[OrderMode.DELIVERY].byDeliveryAddress = venues;
		this.venuesByOrderMode$.next(venuesByOrderMode);
	}

	async initLegal() {
		if (this.legal) return;
		this.legal = await firstValueFrom(this.venuesApi.getLegal('6a577c932adc495b67635739'));
	}
}
